import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { loadImageFromFile } from '../lib/imageUtils';
import { extractDominantColors } from '../lib/colorUtils';
import { useAudioEngine } from '../hooks/useAudioEngine';

const SpinCtx = createContext(null);

const DEFAULT_DISC = {
  imageEl: null,
  imageUrl: null,
  fileName: null,
  designMode: 'shape', // 'shape' | 'vinyl'
  shape: 'circle', // circle | square | roundedSquare | hexagon | star
  size: '12in', // CD | 7in | 10in | 12in | 16in
  rpm: 33 + 1 / 3,
  reverse: false,
  zoom: 1,
  spindleVisible: true,
  centerHole: { enabled: false, sizePct: 6, fill: 'transparent', color: '#17151a' },
  vinyl: { color: '#151515', finish: 'solid', secondaryColor: '#e8e2d0', seed: 42 },
};

const DEFAULT_BACKGROUND = {
  type: 'color', // 'color' | 'image'
  color: '#17151a',
  imageEl: null,
  imageUrl: null,
  dominantColors: [],
};

const DEFAULT_FOREGROUND = { imageEl: null, imageUrl: null, fileName: null, scale: 0 };

const DEFAULT_EXPORT = { aspect: 'vertical' };

export function SpinProvider({ children }) {
  const [disc, setDiscState] = useState(DEFAULT_DISC);
  const [background, setBackgroundState] = useState(DEFAULT_BACKGROUND);
  const [foreground, setForegroundState] = useState(DEFAULT_FOREGROUND);
  const [exportSettings, setExportState] = useState(DEFAULT_EXPORT);
  const [errors, setErrors] = useState({ disc: null, background: null, foreground: null });
  const audio = useAudioEngine();

  const updateDisc = useCallback((partial) => setDiscState((s) => ({ ...s, ...partial })), []);
  const updateVinyl = useCallback((partial) => setDiscState((s) => ({ ...s, vinyl: { ...s.vinyl, ...partial } })), []);
  const updateCenterHole = useCallback((partial) => setDiscState((s) => ({ ...s, centerHole: { ...s.centerHole, ...partial } })), []);
  const updateBackground = useCallback((partial) => setBackgroundState((s) => ({ ...s, ...partial })), []);
  const updateForeground = useCallback((partial) => setForegroundState((s) => ({ ...s, ...partial })), []);
  const updateExport = useCallback((partial) => setExportState((s) => ({ ...s, ...partial })), []);

  const uploadDiscImage = useCallback(async (file) => {
    setErrors((e) => ({ ...e, disc: null }));
    try {
      const { img, url, name } = await loadImageFromFile(file);
      setDiscState((s) => ({ ...s, imageEl: img, imageUrl: url, fileName: name, file }));
      try {
        const colors = extractDominantColors(img, 5);
        setBackgroundState((s) => ({ ...s, dominantColors: colors }));
      } catch (e) { /* non-fatal */ }
    } catch (e) {
      setErrors((er) => ({ ...er, disc: e.message }));
    }
  }, []);

  const clearDiscImage = useCallback(() => {
    setDiscState((s) => ({ ...s, imageEl: null, imageUrl: null, fileName: null, file: null }));
  }, []);

  const uploadBackgroundImage = useCallback(async (file) => {
    setErrors((e) => ({ ...e, background: null }));
    try {
      const { img, url, name } = await loadImageFromFile(file);
      setBackgroundState((s) => ({ ...s, type: 'image', imageEl: img, imageUrl: url, fileName: name, file }));
    } catch (e) {
      setErrors((er) => ({ ...er, background: e.message }));
    }
  }, []);

  const clearBackgroundImage = useCallback(() => {
    setBackgroundState((s) => ({ ...s, type: 'color', imageEl: null, imageUrl: null, file: null }));
  }, []);

  const uploadForegroundImage = useCallback(async (file) => {
    setErrors((e) => ({ ...e, foreground: null }));
    try {
      const { img, url, name } = await loadImageFromFile(file);
      setForegroundState((s) => ({ ...s, imageEl: img, imageUrl: url, fileName: name, file, scale: 0 }));
    } catch (e) {
      setErrors((er) => ({ ...er, foreground: e.message }));
    }
  }, []);

  const clearForegroundImage = useCallback(() => {
    setForegroundState((s) => ({ ...s, imageEl: null, imageUrl: null, fileName: null, file: null, scale: 0 }));
  }, []);

  const resetAll = useCallback(() => {
    setDiscState(DEFAULT_DISC);
    setBackgroundState(DEFAULT_BACKGROUND);
    setForegroundState(DEFAULT_FOREGROUND);
    setExportState(DEFAULT_EXPORT);
    setErrors({ disc: null, background: null, foreground: null });
    audio.reset();
  }, [audio]);

  const value = useMemo(() => ({
    disc, background, foreground, exportSettings, errors, audio,
    updateDisc, updateVinyl, updateCenterHole, updateBackground, updateForeground, updateExport,
    uploadDiscImage, clearDiscImage,
    uploadBackgroundImage, clearBackgroundImage,
    uploadForegroundImage, clearForegroundImage,
    resetAll,
  }), [disc, background, foreground, exportSettings, errors, audio,
    updateDisc, updateVinyl, updateCenterHole, updateBackground, updateForeground, updateExport,
    uploadDiscImage, clearDiscImage, uploadBackgroundImage, clearBackgroundImage,
    uploadForegroundImage, clearForegroundImage, resetAll]);

  return <SpinCtx.Provider value={value}>{children}</SpinCtx.Provider>;
}

export function useSpin() {
  const ctx = useContext(SpinCtx);
  if (!ctx) throw new Error('useSpin must be used within SpinProvider');
  return ctx;
}
